import { Injectable } from "@angular/core";
import { Cultivar, Filter, FilterBuilder, FilterPage, FilterQueryMode, FilterSort, Report, ReportService, Status, translateCommonErrorStatus } from "@core/data";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs/operators";

export interface ReportSampleLinkFormStateModel {
    orgKey: string;
    sampleKeys: string[];
    reports: Report[];
    status: Status;

    query: ReportSampleLinkQuery;
    sort: FilterSort;
    page: FilterPage;
}

export interface ReportSampleLinkQuery {
    search: string;
    scionCultivars: Cultivar[];
}

const DEFAULTS: ReportSampleLinkFormStateModel = {
    orgKey: null,
    sampleKeys: [],
    reports: [],
    status: Status.UNINITIALIZED,
    query: {
        search: null,
        scionCultivars: []
    },
    sort: {
        column: 'updatedAt',
        order: 'desc'
    },
    page: {
        index: 0,
        size: 25
    },
}

export class InitReportSampleLinkForm {
    static readonly type = "[ReportSampleLinkForm] Init";
    constructor(public orgKey: string, public sampleKeys: string[]) {}
}

export class QueryReports {
    static readonly type = "[ReportSampleLinkForm] Query Reports";
    constructor(public query: ReportSampleLinkQuery) {}
}

@State<ReportSampleLinkFormStateModel>({
    name: 'report_sample_link_form_state',
    defaults: DEFAULTS,
})
@Injectable()
export class ReportSampleLinkFormState {

    @Selector()
    static query(state: ReportSampleLinkFormStateModel) {
        return state.query;
    }

    constructor(
        private _reportService: ReportService,
    ) { }

    @Action(InitReportSampleLinkForm, {cancelUncompleted: true})
    initReportSampleLinkForm(ctx: StateContext<ReportSampleLinkFormStateModel>, action: InitReportSampleLinkForm) {
        ctx.setState({
            ...DEFAULTS,
            orgKey: action.orgKey,
            sampleKeys: action.sampleKeys,
            status: Status.OK,
        });

        ctx.dispatch(new QueryReports(DEFAULTS.query));
    }

    @Action(QueryReports)
    queryReports(ctx: StateContext<ReportSampleLinkFormStateModel>, action: QueryReports) {
        ctx.patchState({
            query: action.query,
            status: Status.LOADING,
        });

        const state = ctx.getState();
        const filter = this.getCurrentFilter(state);

        return this._reportService.query(filter)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.OK,
                            reports: result.data
                        });
                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error),
                        });
                    }
                )
            );
    }

    private getCurrentFilter(state: ReportSampleLinkFormStateModel): Filter {
        const fb = new FilterBuilder();

        fb.setSort(state.sort);
        fb.setPage(state.page);

        if (state.orgKey) fb.setQuery('ownerOrgKey', state.orgKey);

        if (Array.isArray(state.query.scionCultivars) && state.query.scionCultivars.length > 0) {
            let keys = state.query.scionCultivars.map(i => i.key);
            fb.setQuery('scionCultivarKey', keys, FilterQueryMode.IN);
        }

        if (state.query.search) {
            if (state.query.search.trim()) fb.setQuery('$fuzzy', state.query.search);
        }

        return fb.get();
    }

}
